import { type Environment, fetchQuery, graphql } from 'react-relay';
import type { fetchItamHamWorkspacePermissionsQuery } from './__generated__/fetchItamHamWorkspacePermissionsQuery.graphql.ts';
import { getHamPermissionChecks } from './getHamPermissionChecks.tsx';
import type { PermissionCheckResult } from './PermissionCheckResult.tsx';

type CheckPermissionsResponse =
	fetchItamHamWorkspacePermissionsQuery['response']['assetsVertical_checkPermissions'];

const getPermissionResults = (
	checkPermissions: CheckPermissionsResponse,
): ReadonlyArray<PermissionCheckResult> =>
	checkPermissions?.__typename === 'AssetsVerticalCheckPermissionsSuccess'
		? checkPermissions.results.map(({ resourceId, permissionId, permitted }) => ({
				resourceId,
				permissionId,
				permitted,
			}))
		: [];

type FetchItamHamWorkspacePermissionsInput = {
	readonly environment: Environment;
	readonly cloudId: string;
	readonly assetsVerticalWorkspaceAri: string;
	readonly onCompleted: (results: ReadonlyArray<PermissionCheckResult>) => void;
	readonly onError: () => void;
};

export const fetchItamHamWorkspacePermissions = ({
	environment,
	cloudId,
	assetsVerticalWorkspaceAri,
	onCompleted,
	onError,
}: FetchItamHamWorkspacePermissionsInput): { readonly unsubscribe: () => void } =>
	fetchQuery<fetchItamHamWorkspacePermissionsQuery>(
		environment,
		graphql`
			query fetchItamHamWorkspacePermissionsQuery(
				$cloudId: ID!
				$checks: [AssetsVerticalPermissionCheckInput!]!
			) {
				assetsVertical_checkPermissions(cloudId: $cloudId, checks: $checks)
					@optIn(to: "AssetsVerticalCheckPermissions") {
					__typename
					... on AssetsVerticalCheckPermissionsSuccess {
						results {
							resourceId
							permissionId
							permitted
						}
					}
				}
			}
		`,
		{ cloudId, checks: getHamPermissionChecks(assetsVerticalWorkspaceAri) },
	).subscribe({
		next: (data) => onCompleted(getPermissionResults(data.assetsVertical_checkPermissions)),
		error: onError,
	});
